/**
 * Agents Page
 * HC Agent management and monitoring
 */

import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Typography,
  Button,
  Space,
  Tag,
  Descriptions,
  Statistic,
  Progress,
  Modal,
  message,
  Tooltip,
  Timeline,
} from 'antd';
import {
  RobotOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  SettingOutlined,
  EyeOutlined,
  ThunderboltOutlined,
  BulbOutlined,
  RadarChartOutlined,
  ClockCircleOutlined,

} from '@ant-design/icons';
import LoadingSpinner from '../components/LoadingSpinner';
import { agentApi } from '../api/hcApi';
import type { Agent } from '../types';

const { Title, Text, Paragraph } = Typography;

const agentDescriptions: Record<string, { description: string; icon: React.ReactNode }> = {
  orchestrator: {
    description:
      'Coordinates all HC agents, manages scheduling, and enforces healing policies across the system.',
    icon: <RadarChartOutlined style={{ fontSize: 32, color: '#13c2c2' }} />,
  },
  monitor: {
    description:
      'Continuously monitors system health, collects metrics from sensors, and generates observations when anomalies are detected.',
    icon: <EyeOutlined style={{ fontSize: 32, color: '#1890ff' }} />,
  },
  executor: {
    description:
      'Executes healing actions based on policy rules. Can restart services, scale workers, clear caches, and more.',
    icon: <ThunderboltOutlined style={{ fontSize: 32, color: '#52c41a' }} />,
  },
  analyzer: {
    description:
      'Performs root cause analysis, collects traces, and generates audit reports for system incidents.',
    icon: <SettingOutlined style={{ fontSize: 32, color: '#fa8c16' }} />,
  },
  knowledge: {
    description:
      'Maintains the knowledge graph, stores patterns, tracks history, and clusters related incidents.',
    icon: <BulbOutlined style={{ fontSize: 32, color: '#722ed1' }} />,
  },
  trainer: {
    description:
      'Analyzes labeled observations to train ML models that improve policy accuracy and reduce false positives.',
    icon: <RobotOutlined style={{ fontSize: 32, color: '#eb2f96' }} />,
  },
  communicator: {
    description:
      'Generates reports, sends notifications, and produces daily digests of system health activity.',
    icon: <ClockCircleOutlined style={{ fontSize: 32, color: '#2f54eb' }} />,
  },
};

const defaultAgentDescription = {
  description: 'HC system agent.',
  icon: <RobotOutlined style={{ fontSize: 32, color: '#8c8c8c' }} />,
};

const statusConfig: Record<string, { color: string; text: string }> = {
  active: { color: 'success', text: 'Active' },
  running: { color: 'success', text: 'Running' },
  busy: { color: 'processing', text: 'Busy' },
  idle: { color: 'warning', text: 'Idle' },
  maintenance: { color: 'purple', text: 'Planned' },
  down: { color: 'error', text: 'Down' },
  error: { color: 'error', text: 'Error' },
  stopped: { color: 'default', text: 'Stopped' },
};

const defaultStatus = { color: 'default', text: 'Unknown' };

const Agents: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const response = await agentApi.list();
      if (response.success && response.data) {
        const agentData = Array.isArray(response.data) ? response.data : [];
        setAgents(agentData);
      } else {
        setAgents([]);
      }
    } catch (err) {
      console.warn('Failed to fetch agents:', err);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
    const interval = setInterval(fetchAgents, 60000);
    return () => clearInterval(interval);
  }, []);

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const handleAction = (agent: Agent, action: 'start' | 'stop' | 'restart') => {
    message.loading(`${action === 'restart' ? 'Restarting' : action === 'start' ? 'Starting' : 'Stopping'} ${agent.name}...`, 1.5)
      .then(() => {
        message.success(`${agent.name} ${action === 'restart' ? 'restarted' : action === 'start' ? 'started' : 'stopped'} successfully`);
      });
  };

  const showDetails = (agent: Agent) => {
    setSelectedAgent(agent);
    setDetailsVisible(true);
  };

  if (loading) {
    return <LoadingSpinner tip="Loading agents..." />;
  }

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <RobotOutlined style={{ marginRight: 12 }} />
          HC Agents
        </Title>
        <Text type="secondary">
          Monitor and manage Health Check system agents
        </Text>
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Agents"
              value={agents.length}
              prefix={<RobotOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active"
              value={agents.filter((a) => a.status === 'active' || a.status === 'running').length}
              valueStyle={{ color: '#52c41a' }}
              prefix={<PlayCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Idle"
              value={agents.filter((a) => a.status === 'idle').length}
              valueStyle={{ color: '#faad14' }}
              prefix={<PauseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Avg Uptime"
              value={formatUptime(
                agents.reduce((sum, a) => sum + a.uptime, 0) / agents.length
              )}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Agent Cards */}
      <Row gutter={[16, 16]}>
        {agents.map((agent) => {
          const config = agentDescriptions[agent.type] || defaultAgentDescription;
          const status = statusConfig[agent.status] || defaultStatus;

          return (
            <Col xs={24} md={12} lg={6} key={agent.id}>
              <Card
                hoverable
                style={{ height: '100%' }}
                actions={[
                  <Tooltip title="View Details" key="details">
                    <Button
                      type="text"
                      icon={<SettingOutlined />}
                      onClick={() => showDetails(agent)}
                    />
                  </Tooltip>,
                  agent.status === 'running' ? (
                    <Tooltip title="Stop" key="stop">
                      <Button
                        type="text"
                        danger
                        icon={<PauseCircleOutlined />}
                        onClick={() => handleAction(agent, 'stop')}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Start" key="start">
                      <Button
                        type="text"
                        icon={<PlayCircleOutlined />}
                        onClick={() => handleAction(agent, 'start')}
                      />
                    </Tooltip>
                  ),
                  <Tooltip title="Restart" key="restart">
                    <Button
                      type="text"
                      icon={<ReloadOutlined />}
                      onClick={() => handleAction(agent, 'restart')}
                    />
                  </Tooltip>,
                ]}
              >
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  {config.icon}
                </div>

                <Card.Meta
                  title={
                    <Space>
                      <span>{agent.name}</span>
                      <Tag color={status.color}>{status.text}</Tag>
                    </Space>
                  }
                  description={
                    <div>
                      <Paragraph
                        ellipsis={{ rows: 2 }}
                        type="secondary"
                        style={{ fontSize: 12, marginBottom: 12 }}
                      >
                        {config.description}
                      </Paragraph>

                      <Space
                        direction="vertical"
                        size={4}
                        style={{ width: '100%' }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Uptime
                          </Text>
                          <Text style={{ fontSize: 12 }}>
                            {formatUptime(agent.uptime)}
                          </Text>
                        </div>

                        {agent.metrics.success_rate !== undefined && (
                          <div>
                            <Text
                              type="secondary"
                              style={{ fontSize: 12, display: 'block' }}
                            >
                              Success Rate
                            </Text>
                            <Progress
                              percent={agent.metrics.success_rate}
                              size="small"
                              status="active"
                            />
                          </div>
                        )}

                        {agent.metrics.observations_processed !== undefined && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Observations
                            </Text>
                            <Text style={{ fontSize: 12 }}>
                              {agent.metrics.observations_processed.toLocaleString()}
                            </Text>
                          </div>
                        )}

                        {agent.metrics.actions_taken !== undefined && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Actions
                            </Text>
                            <Text style={{ fontSize: 12 }}>
                              {agent.metrics.actions_taken.toLocaleString()}
                            </Text>
                          </div>
                        )}

                        {agent.metrics.models_trained !== undefined && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Models Trained
                            </Text>
                            <Text style={{ fontSize: 12 }}>
                              {agent.metrics.models_trained}
                            </Text>
                          </div>
                        )}
                      </Space>
                    </div>
                  }
                />
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Agent Details Modal */}
      <Modal
        title={
          <Space>
            <RobotOutlined />
            {selectedAgent?.name} Details
          </Space>
        }
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsVisible(false)}>
            Close
          </Button>,
          <Button
            key="restart"
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => {
              if (selectedAgent) {
                handleAction(selectedAgent, 'restart');
                setDetailsVisible(false);
              }
            }}
          >
            Restart Agent
          </Button>,
        ]}
        width={600}
      >
        {selectedAgent && (
          <div>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Agent ID">
                {selectedAgent.id}
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                {selectedAgent.type.toUpperCase()}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={(statusConfig[selectedAgent.status] || defaultStatus).color}>
                  {(statusConfig[selectedAgent.status] || defaultStatus).text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Uptime">
                {formatUptime(selectedAgent.uptime)}
              </Descriptions.Item>
              <Descriptions.Item label="Last Activity" span={2}>
                {new Date(selectedAgent.last_activity).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5} style={{ marginTop: 24 }}>
              Configuration
            </Title>
            <Card size="small" style={{ background: '#fafafa' }}>
              <pre style={{ margin: 0, fontSize: 12 }}>
                {JSON.stringify(selectedAgent.config, null, 2)}
              </pre>
            </Card>

            <Title level={5} style={{ marginTop: 24 }}>
              Metrics
            </Title>
            <Card size="small" style={{ background: '#fafafa' }}>
              <pre style={{ margin: 0, fontSize: 12 }}>
                {JSON.stringify(selectedAgent.metrics, null, 2)}
              </pre>
            </Card>

            <Title level={5} style={{ marginTop: 24 }}>
              Recent Activity
            </Title>
            <Timeline
              items={[
                {
                  color: 'green',
                  children: 'Agent started successfully',
                },
                {
                  color: 'blue',
                  children: 'Processed 50 observations',
                },
                {
                  color: 'blue',
                  children: 'Health check passed',
                },
                {
                  color: 'gray',
                  children: 'Waiting for next poll interval...',
                },
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Agents;
